import classNames from "classnames";
import {useCallback} from "react";
import {AccordionEventKey} from "react-bootstrap/esm/AccordionContext";
import {get, useFieldArray, useFormContext} from "react-hook-form";
import {FiX} from "react-icons/fi";

import {Accordion} from "@/components/accordion/Accordion.tsx";
import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {Dot} from "@/components/dot/Dot.tsx";
import {RadioField} from "@/components/form/fields/RadioField/RadioField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {ThirdPartyComponent} from "@/lib/queries/checks.ts";
import {ThirdPartyMonitoringType} from "@/lib/types/checks.ts";
import {ThirdPartyComponentv2} from "@/pages/checks/components/form/fields/components/ThirdPartyComponentv2.tsx";
import {ThirdPartySubComponent} from "@/pages/checks/components/form/fields/components/ThirdPartySubComponent.tsx";
import {ThirdPartyComponentSelectField} from "@/pages/checks/components/form/fields/ThirdPartyComponentSelectField.tsx";

import styles from "./ThirdPartyComponentFilter.module.scss";

interface ThirdPartyComponentFilterProps {
  activeAccordion: AccordionEventKey;
  setActiveAccordion: (eventKey: AccordionEventKey) => void;
  groupCount: string;
}

export const ThirdPartyComponentFilter = ({
  activeAccordion,
  setActiveAccordion,
  groupCount,
}: ThirdPartyComponentFilterProps) => {
  const {
    watch,
    setValue,
    clearErrors,
    control,
    formState: {errors},
  } = useFormContext();
  const {prepend, remove, fields} = useFieldArray({control, name: ServiceField.THIRD_PARTY_COMPONENTS});
  const components: ThirdPartyComponent[] = watch(ServiceField.THIRD_PARTY_COMPONENTS) || [];
  const group = watch(ServiceField.THIRD_PARTY_GROUP);
  const getSelectedCount = (component: ThirdPartyComponent) =>
    component.sub_title_count && component.sub_title_count > 1
      ? component.sub_component_monitoring_type === ThirdPartyMonitoringType.ALL
        ? component.sub_title_count
        : component.sub_components?.length || 0
      : 1;
  const totalSelectedCount = components.reduce((sum, component) => sum + getSelectedCount(component), 0);
  const handleRemove = useCallback(
    (index: number) => {
      const activeIndex = typeof activeAccordion === "string" ? parseInt(activeAccordion, 10) : null;
      clearErrors(`${ServiceField.THIRD_PARTY_COMPONENTS}.${index}`);
      if (activeIndex !== null && !isNaN(activeIndex)) {
        if (index === activeIndex) {
          setActiveAccordion(null);
        } else if (index < activeIndex) {
          setActiveAccordion(`${activeIndex - 1}`);
        }
      }
      remove(index);
    },
    [activeAccordion, clearErrors, remove, setActiveAccordion]
  );
  if (!group) return null;

  return (
    <>
      <ThirdPartyComponentSelectField
        className="mt-4"
        groupFieldName={ServiceField.THIRD_PARTY_GROUP}
        name={ServiceField.THIRD_PARTY_COMPONENTS}
        placeholder="Search components..."
        prepend={prepend}
        onChange={(value) => {
          setActiveAccordion("0");
          if (value) {
            clearErrors(ServiceField.THIRD_PARTY_COMPONENTS);
          }
        }}
      />
      <Accordion
        variant="cloudStatus"
        className={classNames(styles.accordion, {"mt-3": components.length > 0})}
        activeKey={activeAccordion}
        onSelect={(eventKey) => setActiveAccordion(eventKey === activeAccordion ? null : eventKey)}
      >
        {fields.map((field, componentIndex) => {
          const component = components[componentIndex];
          if (!component) return null;
          return component.sub_title_count && component.sub_title_count > 1 ? (
            <Accordion.Item key={field.id} eventKey={`${componentIndex}`}>
              <Card variant="cloudStatusv2" className="p-0">
                <Accordion.Header reverse iconInitialAngle={-90} iconActiveAngle={0}>
                  <div className={styles.titleGroup}>
                    <CDNImage
                      className={styles.accordionHeaderImage}
                      alt={`${group.group} logo`}
                      src={group.logo_src}
                    />
                    <div className="pulseTextBodySmallMedium pulse-color-gray-900">
                      {component.display_title || component.title}
                    </div>
                    {get(errors, `${ServiceField.THIRD_PARTY_COMPONENTS}.${componentIndex}`) && (
                      <Dot variant="red" aria-label="Component has errors" className="ms-2" />
                    )}
                  </div>
                  <Chip variant="tag" size="smallMedium">
                    {getSelectedCount(component)} of {component.sub_title_count} Components
                  </Chip>
                  <Icon
                    icon={FiX}
                    className={styles.closeButton}
                    onClick={() => handleRemove(componentIndex)}
                    role="button"
                  />
                </Accordion.Header>
                <Accordion.Body>
                  <Divider />
                  <div className="p-3 d-flex flex-column gap-3">
                    <Button
                      onClick={() => {
                        setValue(
                          `${ServiceField.THIRD_PARTY_COMPONENTS}.${componentIndex}.sub_component_monitoring_type`,
                          ThirdPartyMonitoringType.ALL,
                          {shouldDirty: true, shouldValidate: true}
                        );
                        clearErrors(`${ServiceField.THIRD_PARTY_COMPONENTS}.${componentIndex}.sub_components`);
                      }}
                      variant="cloudStatusS"
                      className={classNames({
                        active: component.sub_component_monitoring_type === ThirdPartyMonitoringType.ALL,
                      })}
                    >
                      <RadioField
                        key={`${field.id}-${componentIndex}-all`}
                        variant="cloudStatus"
                        name={`${ServiceField.THIRD_PARTY_COMPONENTS}.${componentIndex}.sub_component_monitoring_type`}
                        value={ThirdPartyMonitoringType.ALL}
                        label="Monitor all components"
                        labelTypography="bodySmall"
                      />
                    </Button>
                    <Button
                      onClick={() => {
                        setValue(
                          `${ServiceField.THIRD_PARTY_COMPONENTS}.${componentIndex}.sub_component_monitoring_type`,
                          ThirdPartyMonitoringType.SPECIFIC,
                          {shouldDirty: true, shouldValidate: true}
                        );
                      }}
                      variant="cloudStatusS"
                      className={classNames({
                        active: component.sub_component_monitoring_type === ThirdPartyMonitoringType.SPECIFIC,
                      })}
                    >
                      <div className="text-start w-100">
                        <RadioField
                          key={`${field.id}-${componentIndex}-specific`}
                          variant="cloudStatus"
                          name={`${ServiceField.THIRD_PARTY_COMPONENTS}.${componentIndex}.sub_component_monitoring_type`}
                          value={ThirdPartyMonitoringType.SPECIFIC}
                          label="Filter to specific components"
                          labelTypography="bodySmall"
                        />
                        {component.sub_component_monitoring_type === ThirdPartyMonitoringType.SPECIFIC && (
                          <ThirdPartySubComponent componentIndex={componentIndex} group={group} />
                        )}
                      </div>
                    </Button>
                  </div>
                </Accordion.Body>
              </Card>
            </Accordion.Item>
          ) : (
            <ThirdPartyComponentv2
              key={field.id}
              index={componentIndex}
              group={group}
              body={component.display_title || component.title}
              remove={handleRemove}
            />
          );
        })}
      </Accordion>
      {totalSelectedCount > 0 && (
        <>
          <Divider className="my-3" />
          <div className="pulseTextBodySmall pulse-color-gray-600">
            {totalSelectedCount.toLocaleString()} of {groupCount} selected
          </div>
        </>
      )}
    </>
  );
};
